import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Stack,
  Box,
  Tooltip,
  Switch,
  FormControlLabel,
  Divider,
} from "@mui/material";
import {
  Mic,
  MicOff,
  Call,
  CallEnd,
   Remove as RemoveIcon,
} from "@mui/icons-material";
import SelectBoxWithLabel from "../SelectBoxWithLable";
import { StyledLeadContainer } from "../Styled";
import { useCallContext } from "./useCallContext";
import CallerContextPanel, { CustomerContextDetails } from "./CallerContextPanel";
import CallFab from "./components/CallFab";
import ChooseCallDialog from "./components/ChooseCallDialog";
import {
  handleValidateCodevalue,
  telecallingProviders,
} from "../../Utility/constant";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { useIsMobile } from "../../Utility/hooks/useIsMobile";
import { isValidPhoneNumber, searchRequestData } from "../../Utility/apiHelper";
import { splitE164 } from "../../Utility/countryCodes";

import "./GlobalCallOverlay.css";
import TcSectionLoader from "./components/TcSectionLoader";
import PhoneNumberField from "../PhoneNumberField";


const formatTime = (s) => {
  const m = String(Math.floor(s / 60)).padStart(2, "0");
  const sec = String(s % 60).padStart(2, "0");
  return `${m}:${sec}`;
};

const statusLabelMap = {
  idle: "TC_STATUS_IDLE",
  loading: "TC_STATUS_LOADING",
  ready: "TC_STATUS_READY",
  calling: "TC_STATUS_CALLING",
  ringing: "TC_STATUS_RINGING",
  connected: "TC_STATUS_CONNECTED",
  ended: "TC_STATUS_ENDED",
  error: "TC_STATUS_ERROR",
};



const GlobalCallOverlay = () => {
  const {
    status,
    muted,
    duration,
    overlayMode,
    callType,
    callParams,
    isListening,
    incomingCaller,
    activeProvider,
    callSession,
    inputVolumeRef,
    makeCall,
    endCall,
    toggleMute,
    acceptCall,
    rejectCall,
    minimize,
    expand,
    dismissCall,
    completeTrackedCallSession,
    startListening,
    stopListening,
    setActiveProvider,
    pageCallHint,
    setCallType,
    setCallParams,
  } = useCallContext();

  const internationalise = useSelector(
    (state) => state?.internationalise?.currentHashMapData
  );

  const handleMinimize = () => {
    if (callType === "incoming" && callSession?.lifecycle === "ended") {
      completeTrackedCallSession();
    } else {
      minimize();
    }
  };
  const telecallingDetails = useSelector(
    (state) => state?.telecalling?.telecallingDetails?.[0]
  );
  const currentUser = useSelector((state) => state?.owner?.currentuser);
  const isConfigured = !!telecallingDetails?.accountSid;
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useIsMobile();

  const [selectedProvider, setSelectedProvider] = useState(activeProvider || "");
  const [phone, setPhone] = useState({
    phone : "",
    phoneCountryCode : "+91"
  });

  // ── Caller 360° context (fetched on incoming ring) ─────────────────────
  const [callerContext, setCallerContext] = useState(null);
  const [callerContextLoading, setCallerContextLoading] = useState(false);

  const [chooseDialog, setChooseDialog] = useState({
    open: false,
    phone: "",
  });

  const [isSpeaking, setIsSpeaking] = useState(false);
  const volumePollRef = useRef(null);

  useEffect(() => {
    if (status === "connected" && !muted) {
      volumePollRef.current = setInterval(() => {
        setIsSpeaking(inputVolumeRef.current > 0.05);
      }, 150);
    } else {
      clearInterval(volumePollRef.current);
      setIsSpeaking(false);
    }
    return () => clearInterval(volumePollRef.current);
  }, [status, muted, inputVolumeRef]);

  useEffect(() => {
    if (activeProvider) setSelectedProvider(activeProvider);
  }, [activeProvider]);

  useEffect(() => {
    if (overlayMode === "expanded" && callType === "outgoing" && callParams?.phone) {
      let phoneToSet = callParams?.phone || "";
      let countryCodeToSet = callParams?.phoneCountryCode || pageCallHint?.phoneCountryCode || "+91";

      if (phoneToSet && phoneToSet.startsWith("+")) {
        const { phoneCountryCode, phone: splitPhone } = splitE164(phoneToSet);
        if (phoneCountryCode) {
          phoneToSet = splitPhone || phoneToSet;
          countryCodeToSet = phoneCountryCode;
        }
      }

      setPhone(prev => ({
        ...prev,
        phone: phoneToSet,
        phoneCountryCode: countryCodeToSet,
      }));
    }
  }, [overlayMode, callType, callParams, pageCallHint?.phoneCountryCode]);

  const isCallInProgress =
    status === "calling" || status === "ringing" || status === "connected";
  const isIncomingRinging = status === "ringing_incoming";
  const isConnected = status === "connected";
  const micScale = isSpeaking ? 1.15 : 1;
  const hasPersistedSession =
    Boolean(callType) ||
    Boolean(incomingCaller) ||
    Boolean(callParams && Object.keys(callParams).length);

  const incomingPhoneSyncedRef = useRef(false);
  const wasInCall = useRef(false);

  useEffect(() => {
    if (isCallInProgress || isIncomingRinging) {
      wasInCall.current = true;

      if (isIncomingRinging && incomingCaller && !incomingPhoneSyncedRef.current) {
        incomingPhoneSyncedRef.current = true;
        const { phoneCountryCode, phone: phoneDigits } = splitE164(incomingCaller);
        setPhone({
          phone: phoneDigits || incomingCaller,
          phoneCountryCode: phoneCountryCode || "+91",
        });
      }
    } else {
      if (wasInCall.current) {
        wasInCall.current = false;
      }
      incomingPhoneSyncedRef.current = false;
    }
  }, [isCallInProgress, isIncomingRinging, incomingCaller]);

  // ── Fetch caller context when an incoming call rings ──────────────────
  const refreshCallerContext = useCallback((phoneNumber) => {
    if (!phoneNumber) return;
      setCallerContextLoading(true);
    setCallerContext(null);
    const queryparams = { params: { phone: phoneNumber } };
      searchRequestData(
        queryparams,
        `${process.env.REACT_APP_BASE_URL}api/telecalling/caller-details`
      )
        .then(({ data }) => {
          setCallerContext(data);
        })
        .catch(() => {
          setCallerContext(null);
        })
        .finally(() => {
          setCallerContextLoading(false);
        });
  }, []);

  useEffect(() => {
    if (isIncomingRinging && incomingCaller) {
      refreshCallerContext(incomingCaller);
    }
    // Do not clear immediately when call ends so user can still wrap up post-call actions
    // if (!isIncomingRinging && !isConnected) {
    //   setCallerContext(null);
    //   setCallerContextLoading(false);
    // }
  }, [isIncomingRinging, incomingCaller, isConnected, refreshCallerContext]);

  useEffect(() => {
    if (callType === "incoming" && callSession?.lifecycle === "ended") {
      refreshCallerContext(callSession?.phone || incomingCaller);
    }
  }, [callSession?.lifecycle, callSession?.phone, callType, incomingCaller, refreshCallerContext]);

  if (isMobile) return null;



  const checkConfigured = (fallbackPhone = null) => {
    if (!telecallingDetails?.accountSid) {
      if (fallbackPhone) {
        setChooseDialog({ open: true, phone: fallbackPhone });
      } else {
        enqueueSnackbar(
          handleValidateCodevalue(internationalise, "TC_NOT_CONFIGURED") ||
            "Telecalling is not configured",
          { variant: "warning" }
        );
      }
      return false;
    }
    return true;
  };

  const handleProviderChange = (e) => {
    const p = e.target.value;
    setSelectedProvider(p);
    if (p) {
      if (isListening) {
        stopListening();
      }
      setActiveProvider(p);
    }
  };

  const handleMakeCall = () => {
    if (!checkConfigured(phone?.phone || null)) return;
    if (!pageCallHint?.phone) {
      enqueueSnackbar(
        handleValidateCodevalue(internationalise, "TC_OUTGOING_RESTRICTED") ||
          "Outgoing calls can only be initiated from a Lead or Task",
        { variant: "error" }
      );
      return;
    }
    if (!phone?.phone || !selectedProvider || !isListening) {
      enqueueSnackbar(
        handleValidateCodevalue(internationalise, "COMMON_MSG_MANDATORY_FIELDS") ||
          "Please fill all fields and go Online",
        { variant: "error" }
      );
      return;
    }
    if (!isValidPhoneNumber(phone?.phone, phone?.phoneCountryCode || "+91")) {
        enqueueSnackbar(
          handleValidateCodevalue(internationalise, "COMMON_MSG_VALID_PHONE"),
          { variant: "error" }
        );
      return;
    }

  const formattedPhone = `${phone?.phoneCountryCode || "+91"}${phone?.phone}`;

    const { phone: paramsPhone, ...restCallParams } = callParams;

    makeCall({
      ProviderName: selectedProvider,
      ClientId: currentUser?.client?.id,
      userId: currentUser?.userId,
      ...restCallParams,
      phone: formattedPhone,
    });
  };

  const handleOnlineToggle = () => {
    if (isListening) {
      stopListening();
    } else {
      if (!checkConfigured()) return;
      if (!selectedProvider) {
        enqueueSnackbar(
          handleValidateCodevalue(internationalise, "COMMON_MSG_MANDATORY_FIELDS") ||
            "Please select a provider first",
          { variant: "error" }
        );
        return;
      }
      startListening(selectedProvider);
    }
  };

  const handleFabClick = () => {
    // If telecalling is NOT configured (or we are on mobile), always use device dialer natively
    if (isMobile || !telecallingDetails?.accountSid) {
      const phoneToCall = pageCallHint?.phone || "";
      window.location.href = `tel:${phoneToCall}`;
      return;
    }

    if (!isCallInProgress && !isIncomingRinging) {
      if (pageCallHint?.phone) {
        let phoneToSet = pageCallHint.phone;
        let countryCodeToSet = pageCallHint.phoneCountryCode || "+91";

        if (phoneToSet && phoneToSet.startsWith("+")) {
          const { phoneCountryCode, phone: splitPhone } = splitE164(phoneToSet);
          if (phoneCountryCode) {
            phoneToSet = splitPhone || phoneToSet;
            countryCodeToSet = phoneCountryCode;
          }
        }

        setPhone((prev) => ({
          ...prev,
          phone: phoneToSet,
          phoneCountryCode: countryCodeToSet,
        }));
        setCallParams({
          ...pageCallHint,
          phone: phoneToSet,
          phoneCountryCode: countryCodeToSet,
        });
        setCallType("outgoing");
      } else if (hasPersistedSession) {
        expand();
        return;
      } else {
        setPhone({
          phone: "",
          phoneCountryCode: "+91",
        });
        setCallParams({});
        setCallType(null);
      }
    }
    expand();
  };

  const handleOnChange = (event) => {
    const { name, value } = event.target;

    setPhone((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  
  if (overlayMode === "minimized") {
    return (
      <CallFab
        isConfigured={isConfigured}
        isListening={isListening}
        isIncomingRinging={isIncomingRinging}
        isCallInProgress={isCallInProgress}
        handleFabClick={handleFabClick}
        internationalise={internationalise}
      />
    );
  }

  if (chooseDialog.open) {
    return (
      <ChooseCallDialog
        chooseDialog={chooseDialog}
        onClose={() => setChooseDialog({ open: false, phone: "" })}
        isConfigured={isConfigured}
        onChooseApp={() => {
          setPhone(prev=>({
            ...prev,
            phone : chooseDialog.phone
          }));
          if (pageCallHint) setCallParams(pageCallHint);
          setCallType("outgoing");
          setChooseDialog({ open: false, phone: "" });
          expand();
        }}
        internationalise={internationalise}
      />
    );
  }


  // Whether this is an incoming call interaction (including post-call wrap-up view)
  const isIncomingActive = callType === "incoming";

  const dialogTitle =
    callType === "incoming"
      ? (handleValidateCodevalue(internationalise, "TC_INCOMING_CALL") || "Incoming Call")
      : (callParams?.title || handleValidateCodevalue(internationalise, "COMMON_NOTIFY_CUSTOMER") || "Notify Customer");

  return (
    <Dialog
      open={true}
      onClose={(_, reason) => {
        if (reason === "backdropClick" && isCallInProgress) return;
        handleMinimize();
      }}
      maxWidth={isIncomingActive ? "lg" : "sm"}
      fullWidth
      className="call-dialog"
    >
      {/* ── Title bar ──────────────────────────────────────────────────────── */}
      <DialogTitle style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px" }} className="call-dialog__title">
        <Typography variant="h6" className="call-dialog__title-text">{dialogTitle}</Typography>
        <div className="call-dialog__title-actions">
          <Tooltip title={handleValidateCodevalue(internationalise, "COMMON_MINIMIZE") || "Minimize"}>
            <IconButton onClick={handleMinimize} size="small">
              <RemoveIcon />
            </IconButton>
          </Tooltip>
        </div>
      </DialogTitle>

      <DialogContent dividers className="call-dialog__content">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* ── Top row: Customer Details (full width) ─────────────────────── */}
          {isIncomingActive && (
            <Box>
              <CustomerContextDetails
                customers={callerContext?.customers || []}
                internationalise={internationalise}
                isLoading={callerContextLoading}
                minimize={minimize}
              />
              {!callerContextLoading && <Divider sx={{ my: 1 }} />}
            </Box>
          )}

          {/* Two-column layout: left = dialer, right = leads & tasks */}
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>

            {/* ── LEFT column: existing dialer UI (unchanged) ────────────────── */}
            <Box sx={{ flex: isIncomingActive ? '0 0 380px' : 1, minWidth: 0 }}>

            {/* ── Top Row: Provider & Online Toggle (Always present) ────────── */}
            <StyledLeadContainer sx={{ gap: isMobile ? 2 : 2, alignItems: "center" }} className="mb-10 mt-15">
              <Box sx={{ flexGrow: 1 }}>
                <SelectBoxWithLabel
                  label={
                    handleValidateCodevalue(internationalise, "COMMON_TC_PROVIDER") ||
                    "Provider"
                  }
                  name="provider"
                  value={selectedProvider}
                  optionMenuItem={telecallingProviders}
                  handleOnChange={handleProviderChange}
                  disabled={isCallInProgress}
                />
              </Box>
              <FormControlLabel
                 control={
                   <Switch
                     checked={isListening}
                     disabled={isCallInProgress || !selectedProvider}
                     onChange={handleOnlineToggle}
                     name="online"
                     color="primary"
                   />
                 }
                 label={handleValidateCodevalue(
                     internationalise,
                     isListening ? "TC_STATUS_ONLINE" : "TC_STATUS_OFFLINE"
                 ) || (isListening ? "Online" : "Offline")}
              />
            </StyledLeadContainer>

            {/* ── Dial form: Phone Number ─────────────────────────────────── */}
            {!isIncomingRinging && (
              <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
                {(() => {
                  const isDialerDisabled = isCallInProgress || !selectedProvider || !isListening || !pageCallHint?.phone;
                  return (
                    <PhoneNumberField
                      label={handleValidateCodevalue(internationalise, "COMMON_PHONE_NUMBER")}
                      value={phone?.phone}
                      name="phone"
                      disabled={isDialerDisabled}
                      isViewMode={isDialerDisabled}
                      countryCodeName="phoneCountryCode"
                      countryCode={phone?.phoneCountryCode || "+91"}
                      handleOnChange={handleOnChange}
                      sx={{
                        maxWidth: 280,
                        "& .MuiInputBase-input": {
                          textAlign: "left",
                          fontWeight: "bold",
                          letterSpacing: "1px",
                        }
                      }}
                    />
                  );
                })()}
              </Box>
            )}

            {/* ── Incoming call: caller display ──────────────────────────── */}
            {callType === "incoming" && isIncomingRinging && (
              <Box sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: "bold", mb: 0.5 }}>
                  {incomingCaller || "Unknown Caller"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {handleValidateCodevalue(internationalise, "TC_STATUS_INCOMING") ||
                    "Incoming call..."}
                </Typography>
              </Box>
            )}

            {/* ── Duration + status row ──────────────────────────────────── */}
            <Box
              sx={{
                minHeight: 48,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                my: 1,
              }}
            >
              {(isConnected || duration > 0) && (
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "var(--primary-color)",
                    letterSpacing: 2,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatTime(duration)}
                </Typography>
              )}
              {status && !isIncomingRinging && (
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {(() => {
                    let labelKey = statusLabelMap[status] || status;
                    if (status === "idle" && selectedProvider && !isListening) {
                      labelKey = "TC_STATUS_OFFLINE";
                    }
                    return handleValidateCodevalue(internationalise, labelKey) || (labelKey === "TC_STATUS_OFFLINE" ? "Offline" : labelKey);
                  })()}
                </Typography>
              )}
            </Box>

            {/* ── Call control buttons ───────────────────────────────────── */}
            <Stack direction="row" spacing={2} justifyContent="space-evenly" alignItems="center" sx={{ pb: 2 }}>

              {/* Mute */}
              <Box
                sx={{
                  borderRadius: "50%",
                  display: "inline-flex",
                  transform: `scale(${micScale})`,
                  transition: "transform 120ms ease",
                }}
                className={isSpeaking ? "mic-pulse-active" : ""}
              >
                <Tooltip title={handleValidateCodevalue(internationalise, muted ? "COMMON_UNMUTE_CALL" : "COMMON_MUTE_CALL") || (muted ? "Unmute" : "Mute")}>
                  <IconButton
                    onClick={toggleMute}
                    disabled={!isConnected}
                    sx={{
                      width:55,
                      height: 55,
                      borderRadius: "50%",
                      backgroundColor: muted ? "#f44336" : "#e6f4ef",
                      color: muted ? "#fff" : "#1D9E75",
                      "&:hover": {
                        backgroundColor: muted ? "#d32f2f" : "#158f65",
                        color: "#fff",
                      },
                    }}
                  >
                    {muted ? <MicOff /> : <Mic />}
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Call / End — for outgoing and FAB-launched (callType null) */}
              {callType !== "incoming" && (
                <Tooltip title={handleValidateCodevalue(internationalise, isCallInProgress ? "COMMON_END_CALL" : "COMMON_CALL") || (isCallInProgress ? "End Call" : "Call")}>
                  <span>
                    <IconButton
                      onClick={isCallInProgress ? endCall : handleMakeCall}
                      disabled={(!selectedProvider || !isListening || !pageCallHint?.phone || status === "loading") && !isCallInProgress}
                      sx={{
                        width: 55,
                        height: 55,
                        borderRadius: "50%",
                        backgroundColor: isCallInProgress ? "#f44336" : "#1D9E75",
                        color: "#fff",
                        "&:hover": {
                          backgroundColor: isCallInProgress ? "#d32f2f" : "#158f65",
                        },
                      }}
                    >
                      {isCallInProgress ? <CallEnd /> : <Call />}
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              {/* Incoming ringing: Accept / Reject */}
              {callType === "incoming" && isIncomingRinging && (
                <>
                  <Tooltip title={handleValidateCodevalue(internationalise, "COMMON_ACCEPT") || "Accept"}>
                    <Box className="accept-call-pulse">
                      <IconButton
                        onClick={acceptCall}
                        sx={{
                          width: 55, height: 55,
                          borderRadius: "50%",
                          backgroundColor: "#1D9E75", color: "#fff",
                          "&:hover": { backgroundColor: "#158f65" },
                        }}
                      >
                        <Call />
                      </IconButton>
                    </Box>
                  </Tooltip>
                </>
              )}

              {/* Incoming connected: End */}
              {callType === "incoming" && isConnected && (
                <Tooltip title={handleValidateCodevalue(internationalise, "COMMON_END_CALL") || "End Call"}>
                  <IconButton
                    onClick={endCall}
                    sx={{
                      width: 55, height: 55,
                      borderRadius: "50%",
                      backgroundColor: "#f44336", color: "#fff",
                      "&:hover": { backgroundColor: "#d32f2f" },
                    }}
                  >
                    <CallEnd />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Box>{/* end left column */}

          {/* ── RIGHT column: 360° caller context (incoming only) ──────────── */}
          {isIncomingActive && (
            <>
              <Divider orientation="vertical" flexItem />
              <Box sx={{ flex: 1, minWidth: 0, height: '100%', minHeight: 400, position: 'relative', display: 'flex', flexDirection: 'column' }}>
                {callerContextLoading ? (
                  <TcSectionLoader className="tc-panel-loader" />
                ) : (
                  <CallerContextPanel
                    callerContext={callerContext}
                    isLoading={false}
                  />
                )}
              </Box>
            </>
          )}

          </Box>{/* end two-column flex */}
        </Box>{/* end column flex */}
      </DialogContent>
      <DialogActions>
        <Stack display={"flex"} alignItems={"center"} justifyContent={"center"} width={"100%"} flexDirection={"row"}>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default GlobalCallOverlay;
